/**
 * 点赞 / 转发 / 删除的乐观更新。
 * onMutate 改所有缓存 → onError 回滚 → onSuccess 用服务端值覆盖 →
 * onSettled 不强制 refetch（避免列表跳动）。见 design/04-frontend.md。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { postsApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Post, ReplyPolicy } from '@/api/types'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import {
  patchPostEverywhere,
  patchPostsWhere,
  removePostEverywhere,
  replacePostEverywhere,
} from '@/lib/cache'

/** 转发行与原帖共享计数：优先对原帖 id 操作 */
export function targetIdOf(post: Post): string {
  const repostOf = post.repost_of
  if (repostOf && !('deleted' in repostOf)) return repostOf.id
  return post.id
}

export function useLike() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, liked }: { id: string; liked: boolean }) =>
      liked ? postsApi.unlike(id) : postsApi.like(id),

    onMutate: async ({ id, liked }) => {
      await client.cancelQueries({ queryKey: keys.timelineRoot() })
      const snapshot = client.getQueriesData({ queryKey: keys.timelineRoot() })
      const postSnapshot = client.getQueriesData({ queryKey: ['post'] })

      patchPostEverywhere(client, id, (post) => ({
        ...post,
        counts: { ...post.counts, likes: Math.max(0, post.counts.likes + (liked ? -1 : 1)) },
        viewer: post.viewer ? { ...post.viewer, liked: !liked } : post.viewer,
      }))

      return { snapshot, postSnapshot }
    },

    onError: (_error, _vars, context) => {
      context?.snapshot.forEach(([key, data]) => client.setQueryData(key, data))
      context?.postSnapshot.forEach(([key, data]) => client.setQueryData(key, data))
      toast.error(t('toast.actionFailed'))
    },

    onSuccess: (fresh) => replacePostEverywhere(client, fresh),
  })
}

export function useRepost() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, reposted }: { id: string; reposted: boolean }) =>
      reposted ? postsApi.undoRepost(id) : postsApi.repost(id).then((r) => r.post),

    onMutate: async ({ id, reposted }) => {
      await client.cancelQueries({ queryKey: keys.timelineRoot() })
      const snapshot = client.getQueriesData({ queryKey: keys.timelineRoot() })
      const postSnapshot = client.getQueriesData({ queryKey: ['post'] })

      patchPostEverywhere(client, id, (post) => ({
        ...post,
        counts: { ...post.counts, reposts: Math.max(0, post.counts.reposts + (reposted ? -1 : 1)) },
        viewer: post.viewer ? { ...post.viewer, reposted: !reposted } : post.viewer,
      }))

      return { snapshot, postSnapshot }
    },

    onError: (_error, _vars, context) => {
      context?.snapshot.forEach(([key, data]) => client.setQueryData(key, data))
      context?.postSnapshot.forEach(([key, data]) => client.setQueryData(key, data))
      toast.error(t('toast.actionFailed'))
    },

    onSuccess: (fresh, { reposted }) => {
      replacePostEverywhere(client, fresh)
      // 自己的转发行出现 / 消失，主页时间线需要重新取
      client.invalidateQueries({ queryKey: keys.timelineHome() })
      toast.show(reposted ? t('toast.unreposted') : t('toast.reposted'))
    },
  })
}

export function useBookmark() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, bookmarked }: { id: string; bookmarked: boolean }) =>
      bookmarked ? postsApi.unbookmark(id) : postsApi.bookmark(id),

    onMutate: async ({ id, bookmarked }) => {
      await client.cancelQueries({ queryKey: keys.timelineRoot() })
      const snapshot = client.getQueriesData({ queryKey: keys.timelineRoot() })
      const postSnapshot = client.getQueriesData({ queryKey: ['post'] })

      // 书签不计入任何公开计数，只翻 viewer 标记
      patchPostEverywhere(client, id, (post) => ({
        ...post,
        viewer: post.viewer ? { ...post.viewer, bookmarked: !bookmarked } : post.viewer,
      }))

      return { snapshot, postSnapshot }
    },

    onError: (_error, _vars, context) => {
      context?.snapshot.forEach(([key, data]) => client.setQueryData(key, data))
      context?.postSnapshot.forEach(([key, data]) => client.setQueryData(key, data))
      toast.error(t('toast.actionFailed'))
    },

    onSuccess: (fresh, { bookmarked }) => {
      replacePostEverywhere(client, fresh)
      // 书签列表的成员变了，重取而不是手工增删
      client.invalidateQueries({ queryKey: keys.bookmarks() })
      toast.show(bookmarked ? t('toast.unbookmarked') : t('toast.bookmarked'))
    },
  })
}

export function useDeletePost() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (id: string) => postsApi.remove(id),
    onSuccess: (_result, id) => {
      removePostEverywhere(client, id)
      client.removeQueries({ queryKey: keys.post(id) })
      client.invalidateQueries({ queryKey: keys.timelineRoot() })
      toast.show(t('toast.postDeleted'))
    },
    onError: () => toast.error(t('toast.deleteFailed')),
  })
}

/**
 * 置顶 / 取消置顶（D69）。每人一条：置顶新的时，旧那条的 viewer.pinned 也要清掉，
 * 否则它的菜单还会显示「取消置顶」。用户页的置顶位直接重取 —— 它只在第一页，重取不跳。
 */
export function usePinToggle() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      if (pinned) await postsApi.unpin(id)
      else await postsApi.pin(id)
    },

    onSuccess: (_result, { id, pinned }) => {
      patchPostsWhere(
        client,
        (post) => post.viewer?.is_author === true,
        (post) =>
          post.viewer
            ? { ...post, viewer: { ...post.viewer, pinned: !pinned && post.id === id } }
            : post,
      )
      client.invalidateQueries({ queryKey: ['timeline', 'user'] })
      client.invalidateQueries({ queryKey: keys.me() })
      toast.show(pinned ? t('toast.unpinned') : t('toast.pinned'))
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}

/** 改谁可以回复（D65）。整串回复在服务端一起改，这里把缓存里同一线程的帖子一并改掉 */
export function useSetReplyPolicy() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ id, policy }: { id: string; policy: ReplyPolicy }) =>
      postsApi.setReplyPolicy(id, policy),

    onSuccess: (fresh) => {
      patchPostsWhere(
        client,
        (post) => post.id === fresh.id || post.root_id === fresh.id,
        (post) => ({ ...post, reply_policy: fresh.reply_policy }),
      )
      // 我能不能回复每条回复，要由服务端重新算
      client.invalidateQueries({ queryKey: keys.postReplies(fresh.id) })
      toast.show(t('replyPolicy.updated'))
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}

/** 投票（D78）。服务端返回的帖子带着投完之后能看到的票数，直接换上 */
export function useVote() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ postId, optionId }: { postId: string; optionId: string }) =>
      postsApi.vote(postId, optionId),
    onSuccess: (fresh) => {
      patchPostEverywhere(client, fresh.id, (post) => ({ ...post, poll: fresh.poll }))
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t('toast.actionFailed'))
    },
  })
}
